import { Cookie } from 'lucide-react';

import { BRAND } from '@/components/Brand';
import { Button } from '@/design-system/ui/Button';
import { Dialog, DialogContent, DialogHeader } from '@/design-system/ui/Dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/design-system/ui/Tabs';
import { LEGAL_DOC_KEYS, LEGAL_DOC_LABEL, type LegalDocKey, legalDocument } from '@escala/domain/legal';

import { LegalContent, versionLine } from './LegalContent';

/**
 * Os documentos legais numa janela com três abas — Privacidade, Termos e Cookies —, a
 * mesma do login, do convite, do cadastro público e de "Privacidade e dados". Na de
 * Cookies, "Gerenciar cookies" abre as preferências.
 */
export default function LegalDialog({
  doc,
  orgName,
  onDocChange,
  onClose,
  onManageCookies,
}: {
  doc: LegalDocKey;
  orgName?: string;
  onDocChange: (doc: LegalDocKey) => void;
  onClose: () => void;
  onManageCookies: () => void;
}) {
  const current = legalDocument(doc, { orgName });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="h-[min(760px,calc(100dvh-40px))] w-[min(680px,calc(100vw-24px))]">
        <Tabs
          value={doc}
          onValueChange={(value) => onDocChange(value as LegalDocKey)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <DialogHeader title={current.title} description={`${BRAND.full} · ${versionLine(current)}`}>
            <TabsList aria-label="Documentos" className="mt-3">
              {LEGAL_DOC_KEYS.map((key) => (
                <TabsTrigger key={key} value={key}>
                  {LEGAL_DOC_LABEL[key].short}
                </TabsTrigger>
              ))}
            </TabsList>
          </DialogHeader>
          {LEGAL_DOC_KEYS.map((key) => (
            <TabsContent
              key={key}
              value={key}
              className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4.5 max-md:px-4 max-md:py-3.5"
            >
              <LegalContent doc={legalDocument(key, { orgName })} />
              {key === 'cookies' ? (
                <Button variant="secondary" className="self-start" onClick={onManageCookies}>
                  <Cookie aria-hidden />
                  Gerenciar cookies
                </Button>
              ) : null}
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
